import { useContext, useRef, useState } from "react";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from "react-icons/md";
import LogicContext from "../../Context/LogicContext";
import { DocumentDeleteModal, ModalShell, PagerButton } from "./EmployeeDocumentsTab";

const PAGE_SIZES = [5, 10, 20];

const STATUS_STYLES = {
  Active: "bg-green-100 text-green-700",
  Approved: "bg-green-100 text-green-700",
  Pending: "bg-orange-100 text-orange-600",
  Rejected: "bg-red-100 text-red-600",
  Lost: "bg-red-100 text-red-600",
  Damaged: "bg-red-100 text-red-600",
};

function formatValue(value, type) {
  if (!value) return "-";
  if (type === "date") {
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }
  return value;
}

function emptyForm(fields) {
  return Object.fromEntries(fields.map((field) => [field.key, ""]));
}

function RecordFormModal({ config, mode, initial, onSubmit, onClose }) {
  const [form, setForm] = useState(initial ?? emptyForm(config.fields));
  const [error, setError] = useState("");

  function setField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
    setError("");
  }

  function handleSubmit(event) {
    event.preventDefault();
    const values = Object.fromEntries(config.fields.map((field) => [field.key, String(form[field.key] ?? "").trim()]));
    const missing = config.fields.find((field) => field.required && !values[field.key]);
    if (missing) return setError(`${missing.label} is required.`);
    const customError = config.validate?.(values);
    if (customError) return setError(customError);
    onSubmit(values);
  }

  return (
    <ModalShell open onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="px-6 pt-6">
          <h2 className="text-xl font-bold text-gray-900">
            {mode === "edit" ? "Edit" : "Add"} {config.modalNoun ?? config.singular}
          </h2>
          {config.fields.map((field) => (
            <div key={field.key}>
              <label className="mt-4 block text-xs font-medium text-gray-800">
                {field.label}
                {field.required ? "*" : ""}
              </label>
              {field.type === "select" ? (
                <select
                  value={form[field.key]}
                  onChange={(event) => setField(field.key, event.target.value)}
                  className="mt-1 h-10 w-full rounded-md border-0 bg-slate-100 px-3 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select...</option>
                  {field.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : field.type === "textarea" ? (
                <textarea
                  value={form[field.key]}
                  onChange={(event) => setField(field.key, event.target.value)}
                  maxLength={field.maxLength}
                  placeholder="Input Text"
                  rows={12}
                  className="mt-1 w-full resize-none rounded-md bg-slate-100 p-3 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              ) : (
                <input
                  type={field.type === "date" ? "date" : "text"}
                  value={form[field.key]}
                  onChange={(event) => setField(field.key, event.target.value)}
                  maxLength={field.maxLength}
                  placeholder={field.type === "date" ? "Select Date" : "Input Text"}
                  className="mt-1 h-10 w-full rounded-md bg-slate-100 px-3 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              )}
            </div>
          ))}
          {error ? <p className="mt-3 text-xs text-red-600">{error}</p> : null}
        </div>
        <div className="mt-5 flex items-center justify-between border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-md bg-slate-100 px-5 text-xs font-medium text-gray-700 hover:bg-slate-200"
          >
            Cancel
          </button>
          <button type="submit" className="h-9 rounded-md bg-blue-600 px-5 text-xs font-medium text-white hover:bg-blue-700">
            {mode === "edit" ? "Save changes" : "Save"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function RecordViewModal({ config, record, employeeName, onClose }) {
  return (
    <ModalShell open onClose={onClose}>
      <div className="p-6">
        <h2 className="text-xl font-bold text-gray-900">{config.viewTitle(employeeName)}</h2>
        <dl className="mt-6 space-y-2.5 text-xs">
          {(config.viewFields ?? config.fields).map((field) => (
            <div key={field.key} className="grid grid-cols-[130px_1fr] gap-2">
              <dt className="text-gray-500">{field.label}</dt>
              <dd className="whitespace-pre-wrap break-words font-semibold text-gray-900">
                {formatValue(record[field.key], field.type)}
              </dd>
            </div>
          ))}
        </dl>
        <div className="mt-8 flex justify-end">
          <button type="button" onClick={onClose} className="h-9 rounded-md bg-blue-600 px-5 text-xs font-medium text-white hover:bg-blue-700">
            Close
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function EmployeeRecordTab({ config, employeeName }) {
  const logicContext = useContext(LogicContext);
  const [records, setRecords] = useState(() => config.sample.map((record, index) => ({ ...record, id: index + 1 })));
  const [modal, setModal] = useState(null); // { kind: "add" | "edit" | "view" | "delete", record? }
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [page, setPage] = useState(1);
  const nextId = useRef(config.sample.length + 1);

  const pageCount = Math.max(1, Math.ceil(records.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const rows = records.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const firstIndex = (currentPage - 1) * pageSize;
  const label = config.singular;

  function handleAdd(values) {
    setRecords((current) => [...current, { ...values, ...config.prepare?.(values), id: nextId.current++ }]);
    setModal(null);
    logicContext.showToast(`${label} added successfully`, "success");
  }

  function handleEdit(values) {
    const targetId = modal.record.id;
    setRecords((current) =>
      current.map((record) => (record.id === targetId ? { ...record, ...values, ...config.prepare?.(values) } : record))
    );
    setModal(null);
    logicContext.showToast(`${label} updated successfully`, "success");
  }

  function handleDelete() {
    const targetId = modal.record.id;
    setRecords((current) => current.filter((record) => record.id !== targetId));
    setModal(null);
    logicContext.showToast(`${label} deleted successfully`, "success");
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">{config.title}</h2>
        {!config.readOnly ? (
          <button
            type="button"
            onClick={() => setModal({ kind: "add" })}
            className="flex items-center gap-1 border-b border-blue-500 pb-0.5 text-xs font-medium text-blue-500"
          >
            <FiPlus className="h-3.5 w-3.5" />
            Add New
          </button>
        ) : null}
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-xs">
          <thead>
            <tr className="bg-slate-100 text-gray-800">
              <th className="w-12 rounded-l-md px-3 py-3 font-semibold">#</th>
              {config.columns.map((column, columnIndex) => (
                <th
                  key={column.key}
                  className={`px-3 py-3 font-semibold ${config.readOnly && columnIndex === config.columns.length - 1 ? "rounded-r-md" : ""}`}
                >
                  {column.label}
                </th>
              ))}
              {!config.readOnly ? (
                <th className="rounded-r-md px-3 py-3 text-center font-semibold">Actions</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={config.columns.length + (config.readOnly ? 1 : 2)} className="px-3 py-10 text-center text-sm text-gray-500">
                  No {config.title.toLowerCase()} records yet.
                </td>
              </tr>
            ) : (
              rows.map((record, index) => (
                <tr
                  key={record.id}
                  onClick={() => setModal({ kind: "view", record })}
                  className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-slate-50"
                >
                  <td className="px-3 py-4 text-gray-800">{firstIndex + index + 1}</td>
                  {config.columns.map((column) => (
                    <td
                      key={column.key}
                      className={`px-3 py-4 text-gray-800 ${column.type === "clamp" ? "max-w-[420px]" : ""}`}
                    >
                      {column.type === "clamp" ? (
                        <p className="line-clamp-2 break-words">{record[column.key] || "-"}</p>
                      ) : column.type === "status" ? (
                        <span
                          className={`inline-block rounded-full px-3 py-1 text-[11px] font-medium ${
                            STATUS_STYLES[record[column.key]] ?? "bg-slate-200 text-slate-600"
                          }`}
                        >
                          {record[column.key] || "-"}
                        </span>
                      ) : column.type === "person" ? (
                        <>
                          <p className="font-medium text-gray-900">{record[column.key] || "-"}</p>
                          <p className="max-w-[200px] truncate text-[11px] text-blue-500">{record[column.subKey] || "-"}</p>
                        </>
                      ) : (
                        formatValue(record[column.key], column.type)
                      )}
                    </td>
                  ))}
                  {!config.readOnly ? (
                  <td className="px-3 py-4" onClick={(event) => event.stopPropagation()}>
                    <div className="flex items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => setModal({ kind: "edit", record })}
                        aria-label={`Edit ${label}`}
                        className="text-gray-700 hover:text-blue-500"
                      >
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setModal({ kind: "delete", record })}
                        aria-label={`Delete ${label}`}
                        className="rounded bg-red-50 p-1.5 text-red-500 hover:bg-red-100"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
        <p>{records.length} record(s)</p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <label className="flex items-center gap-2">
            Rows per page
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className="h-8 w-16 rounded border border-gray-200 bg-slate-50 px-2 text-xs text-gray-800 focus:outline-none"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <span>
            Page {currentPage} of {pageCount}
          </span>
          <div className="flex items-center">
            <PagerButton label="First page" onClick={() => setPage(1)} disabled={currentPage === 1}>
              <MdKeyboardDoubleArrowLeft className="h-4 w-4" />
            </PagerButton>
            <PagerButton label="Previous page" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>
              <MdKeyboardArrowLeft className="h-4 w-4" />
            </PagerButton>
            <PagerButton label="Next page" onClick={() => setPage(currentPage + 1)} disabled={currentPage === pageCount}>
              <MdKeyboardArrowRight className="h-4 w-4" />
            </PagerButton>
            <PagerButton label="Last page" onClick={() => setPage(pageCount)} disabled={currentPage === pageCount}>
              <MdKeyboardDoubleArrowRight className="h-4 w-4" />
            </PagerButton>
          </div>
        </div>
      </div>

      {modal?.kind === "add" ? (
        <RecordFormModal config={config} mode="create" onSubmit={handleAdd} onClose={() => setModal(null)} />
      ) : null}
      {modal?.kind === "edit" ? (
        <RecordFormModal config={config} mode="edit" initial={modal.record} onSubmit={handleEdit} onClose={() => setModal(null)} />
      ) : null}
      {modal?.kind === "view" ? (
        <RecordViewModal config={config} record={modal.record} employeeName={employeeName} onClose={() => setModal(null)} />
      ) : null}
      {modal?.kind === "delete" ? <DocumentDeleteModal onConfirm={handleDelete} onClose={() => setModal(null)} /> : null}
    </>
  );
}

export default EmployeeRecordTab;
